import React, { useContext, useEffect, useState } from 'react';
import { client } from 'shared';
import { LocationListLayout, LocationRow } from '../shared';

import Memo from './Memo';
import { BannedWorkerModalContext } from './BannedWorkerModalContext';

import type { BusinessLocationBannedWorker } from 'models';

interface ILocationCheckbox {
    [key: string]: boolean;
}

interface IBusinessOutlawRequest {
    business_id: number;
    business_outlaw_id: number;
    business_outlaw_locations: ILocationParam[];
    memo?: string | null;
}

interface ILocationParam {
    business_location_id: string;
    business_outlaw_id: number;
    memo?: string | null;
    is_business_outlaw: boolean;
}

export default function LocationsList() {
    const {
        isAdmin,
        business,
        businessLocationId,
        provider,
        locations,
        triggerClick,
        setTriggerClick,
        setLocations,
        setError,
        prePrompt,
        onSuccess,
        onClose,
    } = useContext(BannedWorkerModalContext);

    const [locationCheckboxes, setLocationCheckboxes] = useState(getCheckboxes(false, false));
    const [businessMemo, setBusinessMemo] = useState<string | null>(locations?.[0]?.business_memo ?? null);
    const [locationMemos, setLocationMemos] =
        useState<Record<string, { memo: string | null; dirty: boolean }>>(getMemos());
    const [dirtyMemo, setDirtyMemo] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    function setCheckbox(checked: boolean, id: string) {
        setLocationCheckboxes({ ...locationCheckboxes, [`${id}`]: checked });
        setLocationMemos({ ...locationMemos, [`${id}`]: { memo: locationMemos[`${id}`].memo, dirty: true } });
    }

    function setLocationMemo(id: number, memo: string) {
        setLocationMemos({ ...locationMemos, [`${id}`]: { memo, dirty: true } });
        setDirtyMemo(true);
    }

    function getCheckboxes(checked: boolean = true, useChecked: boolean = true) {
        const checkboxes: ILocationCheckbox = {};
        locations.map(
            (location: BusinessLocationBannedWorker) =>
                (checkboxes[`${location.id}`] = useChecked ? checked : location.is_business_outlaw),
        );
        return checkboxes;
    }

    function getMemos(newLocations: BusinessLocationBannedWorker[] = locations) {
        const memos: Record<string, { memo: string | null; dirty: boolean }> = {};
        newLocations.map(
            (location: BusinessLocationBannedWorker) =>
                (memos[`${location.id}`] = { memo: location.memo ?? null, dirty: false }),
        );
        return memos;
    }

    async function onClick() {
        const failedPrePrompt =
            !isAdmin && !Object.values(locationCheckboxes).every((value) => !value) ? await prePrompt() : false;

        if (!failedPrePrompt) {
            setSubmitting(true);

            const requestParams: IBusinessOutlawRequest = {
                ...{
                    business_id: business.id,
                    business_outlaw_id: provider.id,
                    business_outlaw_locations: [],
                },
                ...(isAdmin ? { memo: businessMemo ?? null } : {}),
            };

            requestParams.business_outlaw_locations = Object.entries(locationCheckboxes).map(
                (locationCheckbox: [string, boolean]) => ({
                    ...{
                        business_location_id: locationCheckbox[0],
                        business_outlaw_id: provider.id,
                        is_business_outlaw: locationCheckbox[1],
                    },
                    ...(isAdmin ? { memo: locationMemos[locationCheckbox[0]]?.memo ?? null } : {}),
                }),
            );

            const url = isAdmin
                ? 'internal/business-outlaw/update-locations/'
                : 'api/business-outlaws/update-locations/';
            client(url, {
                method: 'POST',
                body: requestParams,
            })
                .then((response: BusinessLocationBannedWorker[]) => {
                    setLocations(response);
                    setLocationMemos(getMemos(response));
                    setBusinessMemo(response?.[0]?.business_memo ?? null);
                    onClose();
                    onSuccess(response);
                })
                .catch(() => setError(true))
                .finally(() => setSubmitting(false));
        }
    }

    useEffect(() => {
        if (triggerClick) {
            setTriggerClick(false);
            onClick();
        }
    }, [triggerClick]);

    return (
        <LocationListLayout
            businessName={business.name}
            allLabel="Block from all locations"
            allChecked={Object.values(locationCheckboxes).every((value) => value)}
            onToggleAll={(checked) => {
                setLocationCheckboxes(getCheckboxes(checked, true));
                setDirtyMemo(true);
            }}
            businessExtra={<Memo memo={businessMemo} dirty={dirtyMemo} setMemo={(_, memo) => setBusinessMemo(memo)} />}
            onCancel={onClose}
            onSubmit={onClick}
            submitting={submitting}
        >
            {locations.map((location: BusinessLocationBannedWorker) => (
                <LocationRow
                    key={location.id}
                    highlight={businessLocationId === location.id}
                    createdBy={location.business_outlaw_created_by}
                    name={location.name}
                    addressLine={`${location.address.street}, ${location.address.city}, ${location.address.state}`}
                    checked={locationCheckboxes[`${location.id}`]}
                    disabled={submitting}
                    onToggle={(checked) => setCheckbox(checked, location.id.toString())}
                >
                    <Memo
                        dirty={locationMemos[`${location.id}`].dirty}
                        memo={locationMemos[`${location.id}`].memo ?? null}
                        setMemo={setLocationMemo}
                        locationId={location.id}
                    />
                </LocationRow>
            ))}
        </LocationListLayout>
    );
}
